import { CdkCopyToClipboard, Clipboard } from '@angular/cdk/clipboard';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { GameService, InternationalizationService, PlaySoundsService, SessionService, User } from '@app/core/services';
import { environment as env } from 'environments/environment';
import { ShareModule } from 'ngx-sharebuttons';

@Component({
  selector: 'app-final-scores',
  templateUrl: './final-scores.component.html',
  styleUrls: ['./final-scores.component.scss'],
  imports: [RouterModule, CdkCopyToClipboard, ShareModule]
})
export class FinalScoresComponent implements OnInit, OnDestroy {
  public score: number;
  public host: string;
  public currentUser: User;
  public shareMessage: string;
  public showShareForm: boolean;
  public isNewHighScore: boolean;

  private gameService = inject(GameService);
  private router = inject(Router);
  public i18n = inject(InternationalizationService);
  public sessionService = inject(SessionService);
  public soundsService = inject(PlaySoundsService);
  public clipboard = inject(Clipboard);

  ngOnInit(): void {
    this.host = env.BASE_API_URL + '/jeopardy';
    this.currentUser = this.sessionService.getUser();
    this.showShareForm = false;

    this.score = this.currentUser.score || 0;

    this.isNewHighScore = this.score > this.currentUser.highestScore || false;

    this.confetti();

    if (!this.soundsService.musicOff) {
      this.soundsService.playMusic('final-score');
    }

    this.shareMessage = this.i18n.str('challengeu.final_scores_achieved', 'Points achieved by {player} today: {number of points}. Play ChallengEU to beat this score.')()
                            .replace('{player}', this.currentUser.username)
                            .replace('{number of points}', this.currentUser.score.toString());
  }

  private getViewportSize() {
    const viewport = window.visualViewport;

    return {
      width: Math.floor(viewport?.width || window.innerWidth || document.documentElement.clientWidth || 0),
      height: Math.floor(viewport?.height || window.innerHeight || document.documentElement.clientHeight || 500),
    };
  }

  private config() {
    const { width: viewportWidth, height: viewportHeight } = this.getViewportSize();
    // Random rotation
    const rotation = Math.floor(Math.random() * 360);
    // Random Scale
    const scale = Math.max(0.45, Math.random());
    // Random width & height between 0 and viewport
    const width = Math.floor(Math.random() * viewportWidth);
    const height = Math.floor(Math.random() * viewportHeight);

    // Random animation-delay
    const delay = Math.floor(Math.random() * Math.max(1, 15));

    return {
      rotation,
      scale,
      width,
      height,
      delay,
    }
  }

  private getRadomColor() {
    const colors = ['#0CD977', '#FF1C1C', '#FF93DE', '#5767ED', '#FFC61C', '#8497B0']
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    return randomColor;
  }

  confetti() {
    const wrapper = document.getElementById("confetti-wrapper");

    if (!wrapper || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const fragment = document.createDocumentFragment();
    const particleCount = this.getViewportSize().width < 768 ? 120 : 250;

    for (let i = 0; i < particleCount; i++) {
      const color = this.getRadomColor();
      const { rotation, scale, width, height, delay } = this.config();

      const confetti = document.createElement('div');
      const piece = document.createElement('span');
      confetti.className = 'confetti';
      piece.className = 'confetti-piece';
      confetti.style.top = height + 'px';
      confetti.style.left = width + 'px';
      confetti.style.animationDelay = `${delay}s`;
      piece.style.backgroundColor = color;
      piece.style.transform = `skew(15deg) rotate(${rotation}deg) scale(${scale})`;

      confetti.appendChild(piece);
      fragment.appendChild(confetti);
    }

    wrapper.appendChild(fragment);
  }

  public playAgain() {
    this.gameService.finishGame();
    this.router.navigate(['main-menu']);
  }

  ngOnDestroy() {
    const el = document.getElementById("confetti-wrapper") || null;

    if (el) {
      while (el.lastElementChild) {
        el.removeChild(el.lastElementChild);
      }
    }

    this.soundsService.pauseMusic();
  }

  public shareResults(event: Event, val: boolean) {
    event.preventDefault();

    this.showShareForm = val;
  }

  public copyMessage(event: Event) {
    event.preventDefault();

    this.clipboard.copy(this.shareMessage);
  }
}
